import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { FacePaths, type FaceMood } from "./BashFace";

/** The four places a kin shows up: a plan step, the live line, a bench chip, the desk. */
export type KinSize = "step" | "line" | "chip" | "desk";

/**
 * Twenty tints, so twenty lanes read apart at a glance; the twenty-first comes back round
 * (founder, 09-13: "atleast 20 pastel gradients should be there").
 *
 * The ladder is ORDERED for consecutive distance, not by hue or lightness, because a real
 * team is three to six: lanes 1-6 are the pairs anyone actually sees together, and the
 * worst consecutive pair measures dE 45. The two tightest pairs anywhere sit at slots 15+.
 */
const TINTS = 20;

/** The box each size draws: 16/5, 18/5, 24/8, 30/9 (width+height / radius). */
const KIN_FACE_SIZE: Record<KinSize, string> = {
  step: "size-4 rounded-[5px]",
  line: "size-4.5 rounded-[5px]",
  chip: "size-6 rounded-[8px]",
  desk: "size-7.5 rounded-[9px]",
};

/**
 * A helper, drawn as Bash's kin (spec 2026-09-06 §3.4): the same face as the
 * brand mark, its eyes round and open in every mood, over the lane's own tint.
 * Never the copper, which stays the one Bash. The tint is the only value the
 * component computes, so it rides the `--kin` custom property that
 * `.bh-face.bh-face--kin` reads (globals.css); everything else is a class.
 *
 * `kin` is the lane's index, not a colour: lane 21 wears the same tint as lane 1.
 * A NEGATIVE index is no lane at all (a report from a helper that has left the
 * roster): it names no tint, so `.bh-face.bh-face--kin`'s own
 * `--kin: var(--fg-subtle)` default stands. Passing it through would have asked for `var(--kin-0)`, a
 * token that does not exist, and undoing that needed an `!important` in
 * another sheet.
 */
export function KinFace({
  kin,
  size = "chip",
  mood = "content",
  title,
  className,
}: {
  kin: number;
  size?: KinSize;
  mood?: FaceMood;
  title?: string;
  className?: string;
}) {
  const tint = kin >= 0 ? (kin % TINTS) + 1 : null;
  return (
    <span
      className={cn(
        "bh-face bh-face--kin",
        `bh-face--kin-${size}`,
        KIN_FACE_SIZE[size],
        className
      )}
      data-mood={mood}
      data-kin={kin}
      style={
        // A negative index names no lane, so it names no tint either and
        // `.bh-face.bh-face--kin`'s own --kin default stands (lifted by the same two
        // stops as every real tint, so an unplaceable face is family, not a foreign material).
        // Only the SEED hue is set per instance. The pastel gradient is derived from it in
        // globals.css, and the ink is one value for every slot on both themes — a pastel
        // box is light by construction, so the per-slot ink the deep ladder needed is gone.
        tint === null ? undefined : ({ "--kin": `var(--kin-${tint})` } as CSSProperties)
      }
      title={title}
      aria-hidden="true"
      data-testid="kin-face"
    >
      <FacePaths />
    </span>
  );
}
